import { MultiDropdown, ToggleSwitch } from '@missionlabs/smartagent-app-components'

import { TimeRange, TimezoneDropdown, ToolBar } from 'components'
import useHasFeature, { useGetFeatureConfig } from 'hooks/useHasFeature'
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppFeatures } from 'store/app/app.features'
import { IFilters } from 'store/metrics/metrics.state'
import { editUser } from 'store/settings/settings.actions'
import { IQueue } from 'store/settings/settings.state'
import RootState from 'store/state'
import { Queue } from 'store/user/user.state'

import { ListItems } from '@missionlabs/smartagent-app-components/dist/MultiDropdown/types'
import { toggleGroupedQueues } from 'store/metrics/metrics.actions'
import { getToolbarOptions } from '../utils'

const timeRangeOptions = [
    { label: 'Midnight to Now', data: 'midnight' },
    { label: '0.5 hour', data: 0.5 },
    { label: '1 hour', data: 1 },
    { label: '2 hours', data: 2 },
]

interface ComponentProps {
    queues: IQueue[] | undefined
    filters: IFilters
    onFilterChange: (filters: Partial<IFilters>) => void
    isRTMPage: boolean
    userQueues: Queue[] | undefined
}

type Props = ComponentProps

const QueueMetricsHeader: React.FC<Props> = ({
    queues,
    filters,
    onFilterChange,
    isRTMPage,
    userQueues,
}) => {
    const hasFeature = useHasFeature()
    const getFeatureConfig = useGetFeatureConfig()

    const { groupsEnabled } = getFeatureConfig(AppFeatures.REALTIME_DATA_QUEUES_REDESIGNED) || {}
    const isQueuesRedesigned = hasFeature(AppFeatures.REALTIME_DATA_QUEUES_REDESIGNED)

    const { timeRange, timeZone, queueIDs, groupIDs } = filters

    const dispatch = useDispatch()
    const agentID = useSelector((state: RootState) => state.user?.agentID)
    const routingProfile = useSelector((state: RootState) => state.user?.routingProfile)
    const areQueuesGrouped = Boolean(groupsEnabled) && useSelector((state: RootState) => state.metrics?.areQueuesGrouped)
    const metricsConfig = useSelector((state: RootState) => state.metrics?.config)

    const queueGroups = useMemo(
        () =>
            metricsConfig?.groups?.map(({ name, groupID }) => ({
                label: name,
                key: groupID,
            })),
        [metricsConfig],
    )

    useEffect(() => {
        if (groupsEnabled && queueGroups?.length) {
            onFilterChange({ groupIDs: queueGroups?.map(({ key }) => key) })
        }
    }, [])

    // This handler is used to handle the change of queueIDs when the groupsEnabled is true
    const onQueueChangeMulti = (queueIDs: string[]) => {
        onFilterChange({
            queueIDs,
        })
    }

    const onGroupChange = (groupIDs: string[]) => {
        onFilterChange({
            groupIDs,
        })
    }

    const onTimezoneChange = (timeZone: string) => {
        dispatch(
            editUser({
                ID: agentID,
                timeZone: timeZone,
                routingProfileID: routingProfile?.ID.split('/').pop(),
            }),
        )
        onFilterChange({ timeZone })
    }

    const options = getToolbarOptions(queues, queueIDs, isRTMPage, userQueues)

    const TimezoneInfo = !isQueuesRedesigned && (
        <div className="row middle" key="timezone-filter">
            <p className="sm-mar">
                <b>Timezone</b>
            </p>
            <TimezoneDropdown
                defaultValue={timeZone}
                onChange={(timeZone) => onTimezoneChange(timeZone)}
            />
        </div>
    )

    const onToggleGrouping = () => {
        dispatch(toggleGroupedQueues())
    }

    return (
        <ToolBar
            text="Queues"
            items={[
                isRTMPage && groupsEnabled && (
                    <ToggleSwitch
                        key={0}
                        label="Grouped Queues"
                        on={areQueuesGrouped}
                        onClick={onToggleGrouping}
                    />
                ),
                (!isRTMPage || !areQueuesGrouped) && options ? (
                    <div className="items-container" aria-label="Queue">
                        <span className="filter-label">Queue</span>
                        <MultiDropdown
                            key={2}
                            selected={options
                                .filter(({ checked }) => checked)
                                .map(({ key }) => key)}
                            options={options as ListItems}
                            placeholder="Select Queues"
                            title="Queues"
                            onChange={onQueueChangeMulti}
                        />
                    </div>
                ) : null,
                isRTMPage && areQueuesGrouped && queueGroups ? (
                    <div className="items-container" aria-label="Group">
                        <span className="filter-label">Group</span>
                        <MultiDropdown
                            key={3}
                            selected={groupIDs}
                            options={queueGroups as ListItems}
                            placeholder="Select Groups"
                            title="Groups"
                            onChange={onGroupChange}
                        />
                    </div>
                ) : null,
                TimezoneInfo,
                <TimeRange
                    key={timeRange}
                    timeRangeOptions={timeRangeOptions}
                    definedTime={timeRange}
                    setTimeRange={(timeRange) => onFilterChange({ timeRange })}
                />,
            ]}
        />
    )
}

export default QueueMetricsHeader
